// Custom DS-Table column rendering the Quote Status: a design-system Pill for a published quote,
// plain "No quote" text otherwise (the public column API has no arbitrary-JSX cell, so we author a
// column following the established DS pattern — see StatusPillColumn — and import the two
// cell/column-support helpers from the dist internals, the accepted web-front convention).
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Pill, PillVariant} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunityTableRow} from '../opportunityTableRows';

export interface QuoteStatusColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	/** Localized label for a published quote's pill. */
	publishedLabel: string;
	/** Localized text for a project without a quote. */
	noQuoteLabel: string;
	width?: string;
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuoteStatusColumn = (_: QuoteStatusColumnType): null => null;

QuoteStatusColumn.toColumnDef = (child: React.ReactElement<QuoteStatusColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {quoteStatus} = row.original;
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-quote-status-cell">
				{quoteStatus === 'PUBLISHED' ? (
					<span data-cy="quote-status-pill">
						<Pill variant={PillVariant.NEUTRAL}>{child.props.publishedLabel}</Pill>
					</span>
				) : (
					child.props.noQuoteLabel
				)}
			</StyledCell>
		);
	},
});

export default QuoteStatusColumn;
